import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Shield, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function AdminRoles() {
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => base44.entities.Role.list('-created_date')
  });

  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;

  const entities = [
    { name: 'Member', label: 'Membros' },
    { name: 'WorshipTeamMember', label: 'Equipe de Louvor' },
    { name: 'MediaTeamMember', label: 'Equipe de Mídia' },
    { name: 'ReceptionTeamMember', label: 'Equipe de Recepção' },
    { name: 'WorshipSchedule', label: 'Escalas de Louvor' },
    { name: 'MediaSchedule', label: 'Escalas de Mídia' },
    { name: 'ReceptionSchedule', label: 'Escalas de Recepção' },
    { name: 'Song', label: 'Hinos' },
    { name: 'Event', label: 'Eventos' },
    { name: 'Group', label: 'Grupos' },
    { name: 'Message', label: 'Comunicados' },
    { name: 'Banner', label: 'Banners' },
    { name: 'Album', label: 'Álbuns' },
    { name: 'Video', label: 'Vídeos' }
  ];

  const actions = ['view', 'create', 'edit', 'delete'];
  const actionLabels = {
    view: 'Visualizar',
    create: 'Criar',
    edit: 'Editar',
    delete: 'Excluir'
  };

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Role.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Papel criado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Role.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Papel atualizado com sucesso!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Role.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Papel excluído com sucesso!');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: {}
    });
    setEditingRole(null);
    setShowForm(false);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions || {}
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error('O nome é obrigatório.');
      return;
    }

    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const togglePermission = (entity, action) => {
    const updated = { ...formData.permissions };
    if (!updated[entity]) {
      updated[entity] = {};
    }
    updated[entity][action] = !updated[entity][action];
    setFormData({ ...formData, permissions: updated });
  };

  const toggleAllForEntity = (entity) => {
    const updated = { ...formData.permissions };
    const allChecked = actions.every(action => updated[entity]?.[action]);
    
    updated[entity] = {};
    actions.forEach(action => {
      updated[entity][action] = !allChecked;
    });
    
    setFormData({ ...formData, permissions: updated });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-lg mx-auto text-center mt-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
          <p className="text-gray-600">Apenas administradores podem gerenciar papéis.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-4xl mx-auto">
          <PageHeader 
            title="Gerenciar Papéis"
            subtitle={`${roles.length} papéis configurados`}
            showBack={true}
            action={
              <Button onClick={() => setShowForm(true)} size="sm" className="bg-white text-purple-700 hover:bg-purple-50">
                <Plus className="w-4 h-4 mr-1" />
                Novo Papel
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {roles.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="Nenhum papel encontrado"
            description="Crie papéis para controlar permissões"
            actionLabel="Criar Papel"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-3">
            {roles.map((role, index) => (
              <motion.div
                key={role.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl p-5 shadow-sm"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-gray-900">{role.name}</h3>
                    </div>
                    {role.description && (
                      <p className="text-sm text-gray-600">{role.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => handleEdit(role)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="text-red-600"
                      onClick={() => {
                        if (confirm('Deseja excluir este papel?')) {
                          deleteMutation.mutate(role.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                  {entities.map(entity => {
                    const perms = role.permissions?.[entity.name] || {};
                    const hasAnyPerm = actions.some(action => perms[action]);
                    if (!hasAnyPerm) return null;

                    return (
                      <div key={entity.name} className="text-xs">
                        <span className="font-medium text-gray-700">{entity.label}:</span>
                        <div className="flex gap-1 mt-1">
                          {actions.map(action => perms[action] && (
                            <span key={action} className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">
                              {actionLabels[action]}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Editar Papel' : 'Novo Papel'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Líder de Louvor"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva as responsabilidades"
                rows={2}
              />
            </div>

            <div>
              <Label className="mb-3 block">Permissões</Label>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-700">Entidade</th>
                      {actions.map(action => (
                        <th key={action} className="text-center p-3 font-medium text-gray-700">
                          {actionLabels[action]}
                        </th>
                      ))}
                      <th className="text-center p-3 font-medium text-gray-700">Todas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {entities.map(entity => (
                      <tr key={entity.name} className="hover:bg-gray-50">
                        <td className="p-3 font-medium text-gray-900">{entity.label}</td>
                        {actions.map(action => (
                          <td key={action} className="text-center p-3">
                            <Checkbox
                              checked={formData.permissions[entity.name]?.[action] || false}
                              onCheckedChange={() => togglePermission(entity.name, action)}
                            />
                          </td>
                        ))}
                        <td className="text-center p-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleAllForEntity(entity.name)}
                          >
                            {actions.every(action => formData.permissions[entity.name]?.[action]) ? (
                              <X className="w-4 h-4" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingRole ? 'Salvar' : 'Criar Papel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

